import { environment } from '../../../environments/environment'
import { supabase } from './cliente'
import { crearSubidorDeImagenes } from '../storage/subidor-de-imagenes'
import { normalizarImagenAJpeg } from '../util/normalizar-imagen'
import {
  rutaTarjetaProveedor,
  rutaColorEstructuraProducto,
  rutaFotoGaleriaProducto,
  rutaTelaFrente,
  rutaTelaReverso,
} from '../storage/rutas-storage'

/**
 * Crear proveedor/producto NUEVO desde el dashboard (pedido de Angie,
 * 2026-09-16, Fase 2 — el dashboard siempre tiene internet real, así que
 * escribe directo a Supabase, sin pasar por la cola offline de Dexie que usa
 * mobile (ver casos-de-uso.ts para el equivalente mobile).
 *
 * Usa la misma función atómica de numeración (`reservar_codigo_proveedor`,
 * ver docs/plan-fix-numeracion-proveedores-offline-online.md) que mobile —
 * como acá siempre hay red, no hace falta código temporal: se pide el
 * definitivo directo, sin renombrado posterior.
 */

async function subirFoto(ruta: string, archivo: File): Promise<string> {
  const jpeg = await normalizarImagenAJpeg(archivo)
  const datos = await jpeg.arrayBuffer()
  const subidor = crearSubidorDeImagenes(environment)
  return subidor.subir(ruta, datos, 'image/jpeg')
}

export interface DatosProveedorDashboard {
  letra: string
  nombre: string
  contacto: string
  telefono: string
  tarjetaArchivo?: File
}

/** Reserva el código real (mismo camino que mobile al sincronizar) y crea el proveedor. */
export async function crearProveedorDashboard(datos: DatosProveedorDashboard): Promise<string> {
  const { data, error: errorRpc } = await supabase.rpc('reservar_codigo_proveedor', { p_letra: datos.letra })
  if (errorRpc) throw errorRpc
  const codigo = data as string

  const tarjetaUrl = datos.tarjetaArchivo ? await subirFoto(rutaTarjetaProveedor(codigo), datos.tarjetaArchivo) : undefined

  const { error } = await supabase.from('proveedores').insert({
    codigo,
    nombre: datos.nombre,
    contacto: datos.contacto,
    telefono: datos.telefono,
    ...(tarjetaUrl ? { foto_tarjeta_url: tarjetaUrl } : {}),
  })
  if (error) throw error

  return codigo
}

/**
 * Cuenta productos reales ya existentes en Supabase para ese proveedor —
 * a diferencia de mobile (que cuenta solo lo local, porque puede estar
 * offline), el dashboard siempre tiene internet, así que este conteo sí
 * refleja la realidad al momento de crear. No es una reserva atómica como la
 * de proveedores (el doc de referencia marca esto como "riesgo menor" y
 * aplazable) — para un solo operador en el dashboard, el riesgo de colisión
 * es marginal; si se detecta colisión real en la práctica, se puede migrar
 * al mismo patrón atómico más adelante.
 */
export async function siguienteNumeroProductoDashboard(proveedorCodigo: string): Promise<number> {
  const { count, error } = await supabase
    .from('productos')
    .select('id', { count: 'exact', head: true })
    .eq('proveedor_codigo', proveedorCodigo)
  if (error) throw error
  return (count ?? 0) + 1
}

export interface TelaDashboard {
  muestrario: string
  colores: string
  tipo: string
  frenteArchivo?: File
  reversoArchivo?: File
}

export interface FotoProductoDashboard {
  archivo: File
  etiqueta: string
}

/**
 * Mismos campos que captura la app móvil al crear un producto (ver
 * `DatosProducto` en casos-de-uso.ts y captura-producto.ts) — a propósito
 * NO incluye categoría/línea/sublínea/precios: en mobile esos quedan vacíos
 * hasta completarse después en el panel "Completar (Director de categoría)"
 * del dashboard, que el producto recién creado ya tiene disponible sin nada
 * extra que construir acá.
 */
export interface DatosProductoDashboard {
  proveedorCodigo: string
  mueble: string
  atributosEspeciales: string[]
  fob?: number
  moq?: string
  unidadesPorContenedor?: string
  colorEstructura: string
  colorEstructuraArchivo?: File
  notas: string
  fotos: FotoProductoDashboard[]
  telas: TelaDashboard[]
}

export async function crearProductoDashboard(datos: DatosProductoDashboard): Promise<string> {
  const numero = await siguienteNumeroProductoDashboard(datos.proveedorCodigo)
  const productoId = `${datos.proveedorCodigo}-P${String(numero).padStart(2, '0')}`

  const colorEstructuraFotoUrl = datos.colorEstructuraArchivo
    ? await subirFoto(rutaColorEstructuraProducto(productoId), datos.colorEstructuraArchivo)
    : undefined

  const fotos: { url: string; etiqueta: string }[] = []
  for (let i = 0; i < datos.fotos.length; i++) {
    const foto = datos.fotos[i]
    const url = await subirFoto(rutaFotoGaleriaProducto(productoId, `f${i}`), foto.archivo)
    fotos.push({ url, etiqueta: foto.etiqueta })
  }

  const { error: errorProducto } = await supabase.from('productos').insert({
    id: productoId,
    proveedor_codigo: datos.proveedorCodigo,
    mueble: datos.mueble,
    fob: datos.fob ?? null,
    moq: datos.moq ? Number(datos.moq) : null,
    unidades_contenedor: datos.unidadesPorContenedor ? Number(datos.unidadesPorContenedor) : null,
    atributos_especiales: datos.atributosEspeciales,
    fotos: fotos.length > 0 ? fotos : null,
    color_estructura: datos.colorEstructura || null,
    notas: datos.notas || null,
    ...(colorEstructuraFotoUrl ? { color_estructura_foto_url: colorEstructuraFotoUrl } : {}),
  })
  if (errorProducto) throw errorProducto

  for (let i = 0; i < datos.telas.length; i++) {
    const tela = datos.telas[i]
    const telaId = `${productoId}-T${String(i + 1).padStart(2, '0')}`
    const fotoFrenteUrl = tela.frenteArchivo ? await subirFoto(rutaTelaFrente(productoId, telaId), tela.frenteArchivo) : undefined
    const fotoReversoUrl = tela.reversoArchivo ? await subirFoto(rutaTelaReverso(productoId, telaId), tela.reversoArchivo) : undefined

    const { error: errorTela } = await supabase.from('telas').insert({
      id: telaId,
      producto_id: productoId,
      muestrario: tela.muestrario || null,
      colores: tela.colores || null,
      tipo: tela.tipo || null,
      ...(fotoFrenteUrl ? { foto_frente_url: fotoFrenteUrl } : {}),
      ...(fotoReversoUrl ? { foto_reverso_url: fotoReversoUrl } : {}),
    })
    if (errorTela) throw errorTela
  }

  return productoId
}
